import { createSelector } from '@reduxjs/toolkit'
import _filter from 'lodash/filter'
import _find from 'lodash/find'
import _orderBy from 'lodash/orderBy'

import { localStorageGet, localStorageRemove, localStorageSet } from '@scouts/helpers'

import {
    SUBSCRIPTION_STATUS_ACTIVE,
    SUBSCRIPTION_STATUS_CANCELLED,
    SUBSCRIPTION_STATUS_PAST_DUE,
    SUBSCRIPTION_STATUS_UNPAID,
    TAX_RETURN_STATUS_CANCELLED,
    TAX_RETURN_STATUS_COMPLETED_NOT_FILED,
    TAX_RETURN_STATUS_FILED,
    TAX_RETURN_STATUS_FILING_NOT_REQUIRED,
    TAX_RETURN_STATUS_SUBMITTED,
} from '@/constants'
import { getCurrentQuarterlyTaxReturnsAnnualSummaryStartYear } from '@/domain/quarterly-tax-returns-annual-summary'
import {
    doesSubscriptionRequireAction,
    getHasSubscriptionReactivationWithPenaltyPeriodPassed,
    hasFirstPaymentAttemptSucceeded,
    hasSubscription,
    isActiveOrRecoverableSubscription,
    isActiveSubscription,
    isCancelledSubscription,
    isPendingSubscription,
    isRecoverableSubscription,
    shouldResubscribe,
    shouldSubscribe,
} from '@/domain/subscription'
import { getIsValidUniqueTaxpayerReference } from '@/domain/tax-office'
import { getCurrentTaxReturnTaxYear } from '@/domain/tax-return'
import { isQuarterlyTaxReturnsStatusAtLeast, isTaxRegistrationStatusAtLeast, isTaxReturnStatusAtLeast } from '@/helpers'
import {
    IdentityVerificationStatus as IdentityVerificationStatusModel,
    SelfEmploymentProfile,
    Subscription,
    SubscriptionService,
    SubscriptionServices,
    TaxRegistrationStatus as TaxRegistrationStatusModel,
    UserProfileSlice,
    Variant,
    VariantType,
} from '@/models'

import { RootState } from '../store'

const pluckSubscriptionId = (_: unknown, { subscriptionId }: { subscriptionId: number }) => subscriptionId

export const getProfileSlice = (state: RootState) => state.profile
export const getSelfEmploymentProfile = (state: RootState) => state.selfEmploymentProfile

const getSubscriptions = (state: RootState) => getProfileSlice(state).subscriptions || []
const getLatestSubscription = (state: RootState) => getProfileSlice(state).latestSubscription
const getSubscriptionStatus = (state: RootState) => getLatestSubscription(state)?.status || null
const getHasCancelledSubscription = (state: RootState) => isCancelledSubscription(getSubscriptionStatus(state))
const getCancelledSubscriptions = (state: RootState) => _filter(getSubscriptions(state), filterCancelledSubscriptions)
const getLatestCancelledSubscription = (state: RootState) =>
    _orderBy(getCancelledSubscriptions(state), ['id'], ['desc'])[0]
export const getActiveSubscription = createSelector([getSubscriptions], (subscriptions) =>
    subscriptions.find((subscription) => isActiveSubscription(subscription.status))
)

export const getTaxReturns = (state: RootState) => getProfileSlice(state).taxReturns || []

export const getQuarterlyTaxReturns = (state: RootState) => getProfileSlice(state).quarterlyTaxReturns || []

export const getQuarterlyTaxReturnsAnnualSummaries = (state: RootState) =>
    getProfileSlice(state).quarterlyTaxReturnsAnnualSummaries || []

export const getSubscriptionById = createSelector(
    [getSubscriptions, pluckSubscriptionId],
    (subscriptions, subscriptionId) => subscriptions.find((subscription) => subscription.id === subscriptionId)
)

export const getOrderedSubscriptions = createSelector([getSubscriptions], (subscriptions) =>
    _orderBy(subscriptions, ['subscriptionCurrentPeriodEnd'], ['desc'])
)

const getIsCancelledSubscriptionReactivationWithPenalty = createSelector(
    [getLatestCancelledSubscription, getHasCancelledSubscription],
    (latestCancelledSubscription, hasCancelledSubscription) => {
        if (!hasCancelledSubscription) return false

        const isCancelledSubscriptionReactivationWithPenalty =
            !!latestCancelledSubscription &&
            !getHasSubscriptionReactivationWithPenaltyPeriodPassed(latestCancelledSubscription.cancellationDateUTC)

        return isCancelledSubscriptionReactivationWithPenalty
    }
)

const getHasTaxReturns = createSelector([getTaxReturns], (taxReturns) => taxReturns.length > 0)

const getHasQuarterlyTaxReturnsAnnualSummaries = createSelector(
    [getQuarterlyTaxReturnsAnnualSummaries],
    (quarterlyTaxReturnsAnnualSummaries) => quarterlyTaxReturnsAnnualSummaries.length > 0
)

const getHasQuarterlyTaxReturns = createSelector(
    [getQuarterlyTaxReturns],
    (quarterlyTaxReturns) => quarterlyTaxReturns.length > 0
)

const getIsSubscriptionServiceOptedIn = (serviceType: SubscriptionServices) =>
    createSelector([getLatestSubscription], (latestSubscription) => {
        if (!latestSubscription?.subscriptionServices) return false
        return latestSubscription.subscriptionServices.includes(serviceType)
    })

export const getIsBookkeepingOptedIn = getIsSubscriptionServiceOptedIn(SubscriptionService.BookkeepingService)
export const getIsPhoneSupportOptedIn = getIsSubscriptionServiceOptedIn(SubscriptionService.PhoneService)

export default createSelector(
    [
        getProfileSlice,
        getSelfEmploymentProfile,
        getSubscriptions,
        getIsCancelledSubscriptionReactivationWithPenalty,
        getTaxReturns,
        getHasTaxReturns,
        getQuarterlyTaxReturns,
        getHasQuarterlyTaxReturns,
        getQuarterlyTaxReturnsAnnualSummaries,
        getHasQuarterlyTaxReturnsAnnualSummaries,
        getSubscriptionStatus,
        getActiveSubscription,
        getIsBookkeepingOptedIn,
        getIsPhoneSupportOptedIn,
    ],
    (
        profile,
        selfEmploymentProfile,
        subscriptions,
        isCancelledSubscriptionReactivationWithPenalty,
        taxReturns,
        hasTaxReturns,
        quarterlyTaxReturns,
        hasQuarterlyTaxReturns,
        quarterlyTaxReturnsAnnualSummaries,
        hasQuarterlyTaxReturnsAnnualSummaries,
        subscriptionStatus,
        activeSubscription,
        isBookkeepingOptedIn,
        isPhoneSupportOptedIn
    ) => {
        const {
            accountantUseIdentityVerification,
            externalApis,
            identityVerificationStatus,
            isSelfEmployed,
            taxRegistrationStatus,
            uniqueTaxpayerReference,
            variant,
        } = profile

        const isReturningCustomer =
            taxReturns.some(({ status }) => isTaxReturnStatusAtLeast(status, TAX_RETURN_STATUS_SUBMITTED)) ||
            subscriptions.some(({ status }) =>
                [
                    SUBSCRIPTION_STATUS_ACTIVE,
                    SUBSCRIPTION_STATUS_CANCELLED,
                    SUBSCRIPTION_STATUS_PAST_DUE,
                    SUBSCRIPTION_STATUS_UNPAID,
                ].includes(status)
            )

        const hasActiveSubscription = isActiveSubscription(subscriptionStatus)

        const { selfEmployedStartDate } = selfEmploymentProfile

        const ongoingTaxReturn = getOngoingTaxReturn(taxReturns)
        const hasOngoingTaxReturn = !!ongoingTaxReturn

        const activeQuarterlyTaxReturnId = getActiveQuarterlyTaxReturnId(quarterlyTaxReturns, hasQuarterlyTaxReturns)
        const activeQuarterlyTaxReturn = quarterlyTaxReturns.find(({ id }) => id === activeQuarterlyTaxReturnId)

        const ongoingQuarterlyTaxReturnsAnnualSummary = getOngoingQuarterlyTaxReturnsAnnualSummary(
            quarterlyTaxReturnsAnnualSummaries
        )
        const hasOngoingQuarterlyTaxReturnsAnnualSummary = !!ongoingQuarterlyTaxReturnsAnnualSummary

        const hasPaidTaxReturns = taxReturns.some(({ hasPaid }) => !!hasPaid)

        const hasPaidQuarterlyTaxReturnsAnnualSummaries = quarterlyTaxReturnsAnnualSummaries.some(
            ({ hasPaid }) => !!hasPaid
        )

        const isTaxRegistrationStarted = isTaxRegistrationStatusAtLeast(
            taxRegistrationStatus,
            TaxRegistrationStatusModel.Started
        )

        const isTaxRegistrationCompleted = isTaxRegistrationStatusAtLeast(
            taxRegistrationStatus,
            TaxRegistrationStatusModel.Complete
        )

        const isTaxRegistrationInProgress =
            taxRegistrationStatus === TaxRegistrationStatusModel.Submitted ||
            taxRegistrationStatus === TaxRegistrationStatusModel.InformationRequested

        const hasConnections = externalApis && externalApis.length > 0

        const hasBeenSubscribedAtLeastOnce = subscriptions.some((subscription) =>
            hasFirstPaymentAttemptSucceeded(subscription.status)
        )

        const hasCancelledSubscription = isCancelledSubscription(subscriptionStatus)

        const hasPendingSubscription = isPendingSubscription(subscriptionStatus)

        const isIdentityVerificationRequired = getIsIdentityVerificationRequired({
            accountantUseIdentityVerification,
            identityVerificationStatus,
            isSelfEmployed,
            variant: variant ?? undefined,
        })

        const hasFiledTaxReturnsWithSelfEmployment = taxReturns.some(
            ({ status, hasSelfEmployment }) => status === TAX_RETURN_STATUS_FILED && hasSelfEmployment
        )

        const canContactAccountant =
            hasPaidTaxReturns || hasPaidQuarterlyTaxReturnsAnnualSummaries || hasSubscription(subscriptions)

        return {
            ...profile,
            canContactAccountant,
            doesSubscriptionRequireAction: doesSubscriptionRequireAction(subscriptionStatus),
            hasSubscription: hasSubscription(subscriptions),
            isActiveOrRecoverableSubscription: isActiveOrRecoverableSubscription(subscriptionStatus),
            isRecoverableSubscription: isRecoverableSubscription(subscriptionStatus),
            shouldResubscribe: shouldResubscribe(subscriptionStatus),
            shouldSubscribe: shouldSubscribe(subscriptionStatus),
            activeQuarterlyTaxReturn,
            activeQuarterlyTaxReturnId,
            activeSubscription,
            ongoingTaxReturn,
            hasOngoingTaxReturn,
            ongoingQuarterlyTaxReturnsAnnualSummary,
            hasOngoingQuarterlyTaxReturnsAnnualSummary,
            isIdentityVerificationRequired,
            hasActiveSubscription,
            hasCancelledSubscription,
            hasAtLeastOneSubmittedQuarterlyTaxReturn: getHasAtLeastOneSubmittedQuarterlyTaxReturn({
                quarterlyTaxReturns,
            }),
            hasConnections,
            hasFiledTaxReturnsWithSelfEmployment,
            hasPaidTaxReturns,
            hasPaidQuarterlyTaxReturnsAnnualSummaries,
            hasPendingSubscription,
            hasQuarterlyTaxReturns,
            hasQuarterlyTaxReturnsAnnualSummaries,
            hasBeenSubscribedAtLeastOnce,
            hasTaxReturns,
            isBookkeepingOptedIn,
            isPhoneSupportOptedIn,
            isCancelledSubscriptionReactivationWithPenalty,
            isPersonalDetailsFilled: getIsPersonalDetailsFilled(profile),
            isReturningCustomer,
            isSelfEmploymentDetailsFilled: getIsSelfEmploymentDetailsFilled(selfEmploymentProfile),
            isTaxRegistrationCompleted,
            isTaxRegistrationInProgress,
            isTaxRegistrationStarted,
            isValidUniqueTaxpayerReference: getIsValidUniqueTaxpayerReference(uniqueTaxpayerReference),
            selfEmployedStartDate,
            subscriptionStatus,
        }
    }
)

const getHasAtLeastOneSubmittedQuarterlyTaxReturn = ({
    quarterlyTaxReturns,
}: {
    quarterlyTaxReturns: UserProfileSlice['quarterlyTaxReturns']
}) => quarterlyTaxReturns?.some(({ status }) => isQuarterlyTaxReturnsStatusAtLeast(status, TAX_RETURN_STATUS_SUBMITTED))

const getActiveQuarterlyTaxReturnId = (
    quarterlyTaxReturns: UserProfileSlice['quarterlyTaxReturns'],
    hasQuarterlyTaxReturns: boolean
) => {
    if (!hasQuarterlyTaxReturns) {
        localStorageRemove('quarterlyTaxReturnId')
        return null
    }

    const quarterlyTaxReturnIdFromLocalStorage = Number(localStorageGet('quarterlyTaxReturnId'))

    if (quarterlyTaxReturns?.some((qtr) => qtr.id === quarterlyTaxReturnIdFromLocalStorage))
        return quarterlyTaxReturnIdFromLocalStorage

    const latestQuarterlyTaxReturnId = getLatestQuarterlyTaxReturnId(quarterlyTaxReturns)

    const latestQuarterlyTaxReturnIdForLocalStorage = String(latestQuarterlyTaxReturnId ?? '')
    localStorageSet('quarterlyTaxReturnId', latestQuarterlyTaxReturnIdForLocalStorage)

    return latestQuarterlyTaxReturnId
}

const getLatestQuarterlyTaxReturnId = (quarterlyTaxReturns: UserProfileSlice['quarterlyTaxReturns']) => {
    const orderedQuarterlyTaxReturns = _orderBy(quarterlyTaxReturns, ['taxYear', 'quarter'], ['desc', 'desc'])
    const [latestQuarterlyTaxReturn] = orderedQuarterlyTaxReturns

    if (!latestQuarterlyTaxReturn) return undefined

    const { id } = latestQuarterlyTaxReturn

    return id
}

const getOngoingQuarterlyTaxReturnsAnnualSummary = (
    quarterlyTaxReturnsAnnualSummaries: UserProfileSlice['quarterlyTaxReturnsAnnualSummaries']
) => {
    const currentQuarterlyTaxReturnsAnnualSummary = quarterlyTaxReturnsAnnualSummaries?.find(
        (element) => element.taxYear === String(getCurrentQuarterlyTaxReturnsAnnualSummaryStartYear())
    )
    if (!currentQuarterlyTaxReturnsAnnualSummary) return null

    const { status: currentQuarterlyTaxReturnsAnnualSummaryStatus } = currentQuarterlyTaxReturnsAnnualSummary
    const isOngoing = ![TAX_RETURN_STATUS_CANCELLED, TAX_RETURN_STATUS_FILED].includes(
        currentQuarterlyTaxReturnsAnnualSummaryStatus
    )
    return isOngoing ? currentQuarterlyTaxReturnsAnnualSummary : null
}

const getOngoingTaxReturn = (taxReturns: UserProfileSlice['taxReturns']) => {
    const currentTaxReturn = _find(taxReturns, {
        taxYear: getCurrentTaxReturnTaxYear(),
    })
    if (!currentTaxReturn) return null

    const { status: currentTaxReturnStatus } = currentTaxReturn
    const isOngoing = ![
        TAX_RETURN_STATUS_CANCELLED,
        TAX_RETURN_STATUS_COMPLETED_NOT_FILED,
        TAX_RETURN_STATUS_FILED,
        TAX_RETURN_STATUS_FILING_NOT_REQUIRED,
    ].includes(currentTaxReturnStatus)
    return isOngoing ? currentTaxReturn : null
}

const getIsIdentityVerificationRequired = ({
    accountantUseIdentityVerification,
    identityVerificationStatus,
    isSelfEmployed,
    variant,
}: {
    variant?: VariantType
    isSelfEmployed?: boolean | null
    identityVerificationStatus?: string
    accountantUseIdentityVerification?: boolean
}) => {
    /**
     * @TODO:ReviewDomainLogic
     * Does it make sense to include the variant as a reason to ignore the identity verification?
     */
    const isVariantTaxReturn = variant === Variant.taxReturn

    const shouldIgnoreIdentityVerification = isVariantTaxReturn || !isSelfEmployed
    if (shouldIgnoreIdentityVerification) return false

    const isIdentityVerificationStarted =
        IdentityVerificationStatusModel.Started === identityVerificationStatus ||
        IdentityVerificationStatusModel.Requested === identityVerificationStatus

    const shouldRequireIdentityVerification =
        accountantUseIdentityVerification && (!identityVerificationStatus || isIdentityVerificationStarted)

    return shouldRequireIdentityVerification
}

const getIsPersonalDetailsFilled = (profile: UserProfileSlice) => {
    const { dateOfBirth, firstName, lastName, phoneNumber, uniqueTaxpayerReference } = profile

    const isValidUniqueTaxpayerReference = getIsValidUniqueTaxpayerReference(uniqueTaxpayerReference)

    if (!dateOfBirth) return false
    if (!firstName) return false
    if (!lastName) return false
    if (!phoneNumber) return false
    if (!isValidUniqueTaxpayerReference) return false

    return true
}

const getIsSelfEmploymentDetailsFilled = (selfEmploymentProfile: SelfEmploymentProfile) => {
    const { businessDescription, requestedSelfEmploymentStartDate } = selfEmploymentProfile

    if (!businessDescription) return false
    if (!requestedSelfEmploymentStartDate) return false

    return true
}

const filterCancelledSubscriptions = ({ status }: Subscription) => isCancelledSubscription(status)
